import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Info } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/hooks/use-error-handler';
import OnboardingWrapper from '@/components/ui/OnboardingWrapper';
import { EXTENDED_STEPS } from '@/utils/onboarding-steps';
import {
    useUpdateArchitectClassification,
    useUpdateSubcontractorClassification,
    useGetArchitect,
    useGetSubcontractor
} from '@/hooks/use-contractors';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';

// Form validation schema
const formSchema = z.object({
    federalTaxClassification: z.string().min(1, 'Federal tax classification is required'),
});

type FormValues = z.infer<typeof formSchema>;

export default function ClassificationStep() {
    const [searchParams] = useSearchParams();
    const { handleError } = useErrorHandler();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const organizationId = searchParams.get('orgId');
    const accountType = searchParams.get('type') as 'architect' | 'subcontractor';

    // Fetch data based on account type
    const { data: architectData, isLoading: isLoadingArchitect } = useGetArchitect(
        accountType === 'architect' ? organizationId : null
    );
    const { data: subcontractorData, isLoading: isLoadingSubcontractor } = useGetSubcontractor(
        accountType === 'subcontractor' ? organizationId : null
    );

    const isLoadingData = isLoadingArchitect || isLoadingSubcontractor;
    const currentData = accountType === 'architect' ? architectData : subcontractorData;

    // API hooks
    const updateArchitectClassification = useUpdateArchitectClassification();
    const updateSubcontractorClassification = useUpdateSubcontractorClassification();

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            federalTaxClassification: '',
        },
    });

    // Prefill form with existing data
    useEffect(() => {
        if (!currentData) return;

        const specificProfile = accountType === 'architect'
            ? (currentData as any).architectSpecificProfile
            : (currentData as any).subcontractorSpecificProfile;

        if (specificProfile && specificProfile.federalClassification) {
            form.reset({
                federalTaxClassification: specificProfile.federalClassification,
            });
        }
    }, [currentData, form, accountType]);

    const handleNext = async (data: FormValues) => {
        console.log('ClassificationStep: handleNext called with data:', data);
        console.log('ClassificationStep: organizationId:', organizationId, 'accountType:', accountType);

        if (!organizationId) {
            console.log('ClassificationStep: No organizationId - allowing to proceed anyway');
            toast({
                title: 'Warning',
                description: 'Organization ID is missing but proceeding...',
                variant: 'warning',
            });
            return true; // Allow navigation to continue
        }

        if (!accountType || (accountType !== 'architect' && accountType !== 'subcontractor')) {
            console.log('ClassificationStep: Invalid account type:', accountType);
            toast({
                title: 'Error',
                description: 'Invalid account type. Please try again.',
                variant: 'destructive',
            });
            return false;
        }

        setIsSubmitting(true);
        try {
            // Prepare the payload
            const payload = {
                federalClassification: data.federalTaxClassification,
            };

            // Call the appropriate API based on account type
            if (accountType === 'architect') {
                await updateArchitectClassification.mutateAsync({
                    architectId: organizationId,
                    data: payload,
                });
            } else if (accountType === 'subcontractor') {
                await updateSubcontractorClassification.mutateAsync({
                    subcontractorId: organizationId,
                    data: payload,
                });
            }

            toast({
                title: 'Classification Saved',
                description: 'Your federal tax classification has been saved successfully.',
                variant: 'success',
            });

            return true;
        } catch (error: any) {
            handleError(error, {
                customMessage: 'Failed to save classification. Please try again.',
            });
            return false;
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <OnboardingWrapper
            currentStep={2}
            totalSteps={7}
            steps={EXTENDED_STEPS}
            accountType={accountType}
            onNext={async () => {
                console.log('ClassificationStep: onNext called');
                const isValid = await form.trigger();
                console.log('ClassificationStep: Form validation result:', isValid);

                if (!isValid) {
                    console.log('ClassificationStep: Form validation failed');
                    return false;
                }

                return new Promise((resolve) => {
                    console.log('ClassificationStep: Submitting form...');
                    form.handleSubmit(async (data) => {
                        console.log('ClassificationStep: Form submitted with data:', data);
                        const result = await handleNext(data);
                        console.log('ClassificationStep: handleNext result:', result);
                        resolve(result !== false);
                    })();
                });
            }}
            isLoading={isSubmitting}
            saveButtonText="Save & Continue"
        >
            <div className="space-y-6">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight text-foreground mb-2">
                        Federal Tax Classification
                    </h2>
                    <p className="text-muted-foreground">
                        Please select your company's federal tax classification. This information is required for tax reporting purposes.
                    </p>
                </div>

                <Form {...form}>
                    <div className="space-y-6">
                        {/* Show loading state while fetching existing data */}
                        {isLoadingData && (
                            <div className="flex items-center justify-center py-4">
                                <div className="w-6 h-6 border-2 border-outline-variant border-t-slate-600 rounded-full animate-spin" />
                                <span className="ml-2 text-muted-foreground">
                                    Loading existing data...
                                </span>
                            </div>
                        )}

                        <FormField
                            control={form.control}
                            name="federalTaxClassification"
                            render={({ field }) => (
                                <FormItem className="space-y-3">
                                    <FormLabel className="text-foreground font-medium">
                                        Company Federal Classification<span className="text-red-600">*</span>
                                    </FormLabel>
                                    <FormControl>
                                        <RadioGroup
                                            onValueChange={field.onChange}
                                            value={field.value}
                                            className="grid gap-4"
                                        >
                                            <div className="flex items-start space-x-3 p-4 border border-border rounded-lg hover:bg-muted">
                                                <RadioGroupItem
                                                    value="sole-proprietor"
                                                    id="sole-proprietor"
                                                    className="mt-1"
                                                />
                                                <div className="flex-1">
                                                    <Label htmlFor="sole-proprietor" className="font-medium text-foreground">
                                                        Sole Proprietor or Single-member LLC
                                                    </Label>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        Individual owner or single-member limited liability company
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-start space-x-3 p-4 border border-border rounded-lg hover:bg-muted">
                                                <RadioGroupItem
                                                    value="partnership"
                                                    id="partnership"
                                                    className="mt-1"
                                                />
                                                <div className="flex-1">
                                                    <Label htmlFor="partnership" className="font-medium text-foreground">
                                                        Partnership or LLC taxed as a Partnership
                                                    </Label>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        Multiple owners sharing profits and losses
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-start space-x-3 p-4 border border-border rounded-lg hover:bg-muted">
                                                <RadioGroupItem
                                                    value="c-corp"
                                                    id="c-corp"
                                                    className="mt-1"
                                                />
                                                <div className="flex-1">
                                                    <Label htmlFor="c-corp" className="font-medium text-foreground">
                                                        C-Corporation or LLC taxed as a C-Corporation
                                                    </Label>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        Corporation with shareholders, subject to double taxation
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-start space-x-3 p-4 border border-border rounded-lg hover:bg-muted">
                                                <RadioGroupItem
                                                    value="s-corp"
                                                    id="s-corp"
                                                    className="mt-1"
                                                />
                                                <div className="flex-1">
                                                    <Label htmlFor="s-corp" className="font-medium text-foreground">
                                                        S-Corporation or LLC taxed as S-Corporation
                                                    </Label>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        Corporation with pass-through taxation
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-start space-x-3 p-4 border border-border rounded-lg hover:bg-muted">
                                                <RadioGroupItem
                                                    value="llc"
                                                    id="llc"
                                                    className="mt-1"
                                                />
                                                <div className="flex-1">
                                                    <Label htmlFor="llc" className="font-medium text-foreground">
                                                        Limited Liability Company (LLC)
                                                    </Label>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        LLC with default tax classification
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-start space-x-3 p-4 border border-border rounded-lg hover:bg-muted">
                                                <RadioGroupItem
                                                    value="other"
                                                    id="other"
                                                    className="mt-1"
                                                />
                                                <div className="flex-1">
                                                    <Label htmlFor="other" className="font-medium text-foreground">
                                                        Other
                                                    </Label>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        Different classification not listed above
                                                    </p>
                                                </div>
                                            </div>
                                        </RadioGroup>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Blue info box */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <Info className="w-6 h-6 text-blue-400" />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-base font-bold text-blue-800">
                                        Not sure about your classification?
                                    </h3>
                                    <p className="mt-1 text-base text-blue-700">
                                        We recommend consulting with your accountant or tax advisor. You can also check your previous tax returns or business formation documents.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </Form>
            </div>
        </OnboardingWrapper>
    );
}
